// components/EffectLayer.js
//
// An invitation's effect on the website: the shared WebGL player
// (shared/effectPlayer.js) in a transparent iframe, playing the stacked-alpha
// video - or, where the browser refuses to autoplay video (Safari on an
// iPhone in Low Power Mode), the effect's Low Power version: an animated WebP
// with real transparency, which still animates there. That file is several
// times the size of the video, so it is only fetched after a refusal, and
// after one refusal the page goes straight to it until the tab is hidden and
// shown again (Low Power Mode may have been switched off meanwhile).
//
// `className` positions it - fixed, full window, at the layer the host chose
// - and is shared by the iframe and the image.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { effectPlayerHtml, effectUrlFor } from '../shared/effectPlayer';

let videoRefused = false;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') videoRefused = false;
  });
}

export default function EffectLayer({ effect, className, title = 'Invitation effect' }) {
  const frameRef = useRef(null);
  const lowPowerUrl = effect?.mobile_webp_url || null;
  const [useLowPower, setUseLowPower] = useState(() => videoRefused && !!lowPowerUrl);

  // Chosen once per effect: the portrait file for a portrait window. A whole
  // HTML document in a string; rebuilding it on every render would restart it.
  const html = useMemo(() => {
    const url = effectUrlFor(effect, { width: window.innerWidth, height: window.innerHeight });
    return url ? effectPlayerHtml(url) : null;
  }, [effect]);

  useEffect(() => { setUseLowPower(videoRefused && !!lowPowerUrl); }, [effect, lowPowerUrl]);

  // The player says when the browser refused to play it.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      const msg = e.data;
      if (msg?.source !== 'invits-effect' || msg.status !== 'failed' || msg.reason !== 'autoplay-blocked') return;
      videoRefused = true;
      if (lowPowerUrl) setUseLowPower(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [lowPowerUrl]);

  if (useLowPower && lowPowerUrl) {
    return <img src={lowPowerUrl} alt="" aria-hidden="true" className={`${className} is-image`} />;
  }
  if (!html) return null;
  return (
    <iframe
      ref={frameRef}
      key={effect.id}
      title={title}
      srcDoc={html}
      className={className}
      aria-hidden="true"
      tabIndex={-1}
    />
  );
}
